/* ============================================================
   PALAMANO — Barra de navegación inferior tipo app (solo móvil)

   Se apoya en lo que el tema ya trae:
   · los botones con aria-controls abren y cierran los drawers solos
     y reciben aria-expanded → el paso de ☰ a ✕ es puro CSS
   · #header-scroll-tracker ya existe para saber si se desplazó,
     sin escuchar el evento scroll
   · <x-drawer-popover> es el componente del tema que sube desde abajo
   ============================================================ */
(() => {
  const MOBILE = '(max-width: 699px)';
  const HERO_SECTIONS = [
    'shopify-section--slideshow',
    'shopify-section--image-with-text-overlay',
    'shopify-section--video',
    'shopify-section--before-after',
    'shopify-section--hot-spots',
  ];

  /* ==========================================================
     1. La barra
     ========================================================== */
  class PalamanoMobileNav extends HTMLElement {
    connectedCallback() {
      /* Se declara dentro del header (para poder leer el menú que el
         comerciante configuró) pero vive en el body, para que ningún
         contenedor del header pueda afectar su posición. */
      if (!this.moved && this.parentElement !== document.body) {
        this.moved = true;
        document.body.appendChild(this);
        return;
      }

      this.state = 'solid';
      this.watchScroll();
      this.watchHero();
      this.watchSheet();

      /* Cuando ya cargó todo, se vuelve a medir: si la foto del hero
         tardó, la primera medida se hizo con la sección a medio alto. */
      window.addEventListener('load', () => this.watchHero());
      window.addEventListener('resize', () => this.watchHero());
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.heroObserver?.disconnect();
    }

    /* Mientras la imagen del hero pasa por detrás del header, el logo (y en
       escritorio también el menú y los iconos) van en su versión clara. En
       cuanto quedan sobre fondo claro vuelven los colores normales: el beige
       sobre el crema del sitio no se ve. */
    watchHero() {
      const first = document.querySelector('#main > .shopify-section');
      const sobre = (valor) => document.documentElement.classList.toggle('pmn-over-hero', valor);

      /* Se puede llamar más de una vez: el hero no tiene su altura final
         hasta que carga la foto, así que la primera evaluación puede dar
         que no hay hero cuando sí lo hay. */
      this.heroObserver?.disconnect();

      if (!first || !this.pageHasHero || !('IntersectionObserver' in window)) {
        sobre(false);
        return;
      }

      const alto = Math.round(document.querySelector('.header')?.getBoundingClientRect().height || 0);
      const barra = Math.round(
        document.querySelector('.shopify-section--announcement-bar')?.getBoundingClientRect().height || 0
      );

      this.heroObserver = new IntersectionObserver(
        ([entrada]) => sobre(entrada.isIntersecting),
        { rootMargin: `0px 0px -${Math.max(0, window.innerHeight - barra - alto)}px 0px`, threshold: 0 }
      );
      this.heroObserver.observe(first);
    }

    /* La barra solo arranca transparente cuando lo primero de la página
       es un hero que llena la pantalla (la portada). No basta con que
       sea un slideshow: el banner corto de una colección deja fondo
       claro justo detrás de la barra y los iconos blancos se perderían. */
    get pageHasHero() {
      const first = document.querySelector('#main > .shopify-section');
      if (!first) return false;

      /* Solo cuentan los heros de imagen. El marcador del tema
         [allow-transparent-header] no sirve para decidir esto: la ficha de
         producto también lo lleva (y el tema ya le monta el header encima),
         pero detrás hay fondo claro, no una foto. */
      if (!HERO_SECTIONS.some((cls) => first.classList.contains(cls))) return false;

      return first.getBoundingClientRect().height >= window.innerHeight * 0.8;
    }

    setState(state) {
      if (this.state === state) return;
      this.state = state;
      this.dataset.state = state;
    }

    refresh(isAtTop) {
      const hero = this.pageHasHero;

      /* El header de arriba usa la misma señal, en móvil y en escritorio:
         solo se solapa con el contenido cuando la página abre con un hero
         a sangre. */
      document.documentElement.classList.toggle('pmn-hero', hero);

      const transparent = isAtTop && hero && window.matchMedia(MOBILE).matches;
      this.setState(transparent ? 'top' : 'solid');

      /* Señal genérica de "ya se desplazó", sin hero de por medio: la usa
         la ficha de producto para encoger el botón de añadir y revelar
         la hamburguesa (palamano-mobile-nav.css, bloque «PÁGINA DE
         PRODUCTO»). Se apoya en el mismo rastreador que ya existía, sin
         sumar un listener de scroll nuevo. */
      document.documentElement.classList.toggle('pmn-scrolled', !isAtTop);
    }

    watchScroll() {
      const tracker = document.getElementById('header-scroll-tracker');

      if (tracker && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => this.refresh(entry.isIntersecting),
          { threshold: 0 }
        );
        this.observer.observe(tracker);
        this.refresh(window.scrollY < 200);
        return;
      }

      const onScroll = () => this.refresh(window.scrollY < 200);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    /* Con el bottom sheet abierto la barra cambia de aspecto y el
       botón de añadir al carrito se aparta. */
    watchSheet() {
      const sheet = document.getElementById('palamano-menu-sheet');
      if (!sheet) return;

      sheet.addEventListener('dialog:before-show', () => {
        document.documentElement.classList.add('pmn-sheet-open', 'pmn-overlay-open');
      });

      /* 7-oct, dos vueltas:
         1ª vuelta: se probó quitar 'pmn-sheet-open' en 'before-hide' (el
         tema lo dispara al toque mismo de cerrar) en vez de 'after-hide'
         (que llega recién cuando el panel YA terminó de deslizarse), para
         que el botón de añadir no se quedara invisible todo ese tiempo.
         Pero 'pmn-sheet-open' también controla el z-index de la barra
         (sube a 1000 para quedar encima del panel, ver CSS) — al quitarla
         de golpe en 'before-hide' la barra volvía a 150 mientras el panel
         (999) seguía visible encima todavía bajando: exactamente el bug
         de la barra tapada que ya se había arreglado antes, pero ahora en
         el cierre. Se veía como "hueco" / contenido raro un instante.
         2ª vuelta: se separan las dos cosas. 'pmn-sheet-open' (z-index,
         diseño de 4 iconos, fondo de la barra) se queda en 'after-hide',
         sin pelear con el panel. Para que el botón de añadir no se quede
         invisible mientras tanto, se suma solo una clase aparte en
         'before-hide' que le fuerza la opacidad de vuelta — esa sí puede
         adelantarse sin romper nada, porque no toca z-index ni layout. */
      sheet.addEventListener('dialog:before-hide', () => {
        document.documentElement.classList.add('pmn-sheet-closing');
      });

      sheet.addEventListener('dialog:after-hide', () => {
        document.documentElement.classList.remove('pmn-sheet-open', 'pmn-overlay-open', 'pmn-sheet-closing');
      });
    }
  }

  if (!window.customElements.get('palamano-mobile-nav')) {
    window.customElements.define('palamano-mobile-nav', PalamanoMobileNav);
  }

  /* ==========================================================
     2. Elegir la variante antes de añadir al carrito

     Si el producto tiene opciones y el cliente todavía no ha tocado
     ninguna, el botón flotante abre un panel para escoger en vez de
     añadir la variante que venía marcada por defecto. Si ya escogió,
     el botón añade directo, sin pasos de más.

     Nada de esto duplica el estado del tema: el panel marca los
     mismos radios del selector de la página y el botón de añadir
     envía el formulario real del producto.
     ========================================================== */
  const SHEET_ID = 'palamano-variant-sheet';
  const touched = new Set();
  let sheet = null;

  const isMobile = () => window.matchMedia(MOBILE).matches;
  const getSticky = () => document.querySelector('product-sticky-add-to-cart');
  const getFormId = () => getSticky()?.getAttribute('main-product-form') || null;

  const getPicker = () => {
    const formId = getFormId();
    if (!formId) return null;
    return (
      document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`) ||
      document.querySelector('variant-picker')
    );
  };

  const optionName = (fieldset) =>
    (fieldset.querySelector('legend')?.textContent || '').replace(/:\s*$/, '').trim();

  const readOptions = () => {
    const picker = getPicker();
    if (!picker) return [];

    /* Una opción con un solo valor no es una elección (p. ej. Tortillas de
       Almendras quedó solo en M al pasar la L a producto aparte, 24-oct). */
    return Array.from(picker.querySelectorAll('fieldset')).map((fieldset) => ({
      name: optionName(fieldset),
      values: Array.from(fieldset.querySelectorAll('input')).map((input) => ({
        input,
        label:
          input.closest('label')?.textContent.trim() ||
          picker.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.textContent.trim() ||
          input.value,
      })),
    })).filter((option) => option.values.length > 1);
  };

  /* ¿Le falta escoger algo? Sí mientras queden opciones que no haya tocado. */
  const needsChoice = () => {
    const options = readOptions();
    if (options.length === 0) return false;
    return options.some((option) => !touched.has(option.name));
  };

  const ensureSheet = () => {
    if (sheet) return sheet;

    sheet = document.getElementById(SHEET_ID);
    if (!sheet) return null;

    sheet.addEventListener('dialog:before-show', () =>
      document.documentElement.classList.add('pmn-overlay-open')
    );
    sheet.addEventListener('dialog:after-hide', () =>
      document.documentElement.classList.remove('pmn-overlay-open')
    );

    return sheet;
  };

  const renderSheet = () => {
    const el = ensureSheet();
    if (!el) return null;

    const formId = getFormId();
    const wrapper = document.createElement('div');
    wrapper.className = 'pmn-variant';

    readOptions().forEach((option) => {
      const group = document.createElement('div');
      group.className = 'pmn-variant__group';

      const heading = document.createElement('p');
      heading.className = 'pmn-variant__title';
      heading.textContent = option.name;
      group.append(heading);

      const list = document.createElement('div');
      list.className = 'pmn-variant__values';

      option.values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pmn-variant__value';
        button.textContent = value.label;
        button.disabled = value.input.disabled;
        if (value.input.checked) button.dataset.selected = 'true';

        button.addEventListener('click', () => {
          touched.add(option.name);
          if (value.input.checked) return;

          /* Se marca el radio real del tema y se avisa con un evento
             'change': el tema lo escucha y actualiza precio, imagen y
             formulario. Un .click() aquí cerraría el panel, porque el
             foco atrapado lo leería como un toque fuera. */
          value.input.checked = true;
          value.input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        list.append(button);
      });

      group.append(list);
      wrapper.append(group);
    });

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'pmn-variant__submit button';
    if (formId) submit.setAttribute('form', formId);
    submit.textContent =
      getSticky()?.querySelector('button[type="submit"]')?.textContent.trim() || 'Añadir a la cesta';
    submit.addEventListener('click', () => el.hide());

    wrapper.append(submit);
    el.replaceChildren(wrapper);

    return el;
  };

  /* El clic se intercepta en fase de captura, antes de que el tema
     envíe el formulario. */
  document.addEventListener(
    'click',
    (event) => {
      if (!isMobile()) return;

      const button = event.target.closest?.('button[type="submit"]');
      if (!button || !button.closest('product-sticky-add-to-cart')) return;
      if (!needsChoice()) return;

      const el = renderSheet();
      if (!el || typeof el.show !== 'function') return;

      event.preventDefault();
      event.stopPropagation();
      el.show({ activator: button });
    },
    true
  );

  /* Si escoge directamente en la página, el botón vuelve a añadir directo. */
  document.addEventListener('change', (event) => {
    const fieldset = event.target.closest?.('variant-picker fieldset');
    if (!fieldset) return;
    const name = optionName(fieldset);
    if (name) touched.add(name);
  });

  /* Al cambiar de variante el tema vuelve a dibujar el producto, así que
     el panel se redibuja con los elementos nuevos sin cerrarse. */
  document.addEventListener('variant:change', () => {
    if (sheet?.hasAttribute('open')) renderSheet();
  });

  /* ==========================================================
     3. El aviso flotante de Shopify Forms («10% OFF»)

     Vive dentro de un shadow DOM, así que una hoja de estilos del
     tema no lo alcanza: hay que inyectarla dentro. Aparece tarde y
     puede volver a montarse, de ahí el observador.
     ========================================================== */
  const FORMS_CSS = `
    @media screen and (max-width: 699px) {
      #app-embed {
        inset-block-end: var(--pmn-total, 60px) !important;
        transition: opacity 0.2s ease;
      }
      :host-context(html.pmn-overlay-open) #app-embed {
        opacity: 0 !important;
        pointer-events: none !important;
      }
      /* Con un drawer del tema abierto no hay barra abajo. */
      :host-context(html.lock) #app-embed {
        inset-block-end: 0 !important;
      }
    }
  `;

  const liftFormsEmbed = () => {
    document.querySelectorAll('shopify-forms-embed').forEach((host) => {
      const root = host.shadowRoot;
      if (!root || root.pmnLifted) return;
      root.pmnLifted = true;

      const style = document.createElement('style');
      style.textContent = FORMS_CSS;
      root.append(style);
    });
  };

  liftFormsEmbed();
  new MutationObserver(liftFormsEmbed).observe(document.body, { childList: true, subtree: false });
  window.addEventListener('load', liftFormsEmbed);
})();

/* ============================================================
   Lupa — animación de puntos con efecto esfera (Antonio, 22-oct,
   referente: app de Zara). Ciclo:
     mango se recoge → aro crece → en un parpadeo el trazo se parte en
     8 puntos → los puntos giran en 3D como un aro alrededor de una esfera
     (los de adelante más grandes y nítidos, los de atrás chicos y
     tenues) → pausa → giran de vuelta → se juntan en aro → sale el mango
     → reposo.
   El paso aro↔puntos usa el stroke-dasharray del aro: con linecap round
   una raya de largo ~0 se pinta como un punto, y en ese instante se
   cambia por los círculos de verdad, que caen en el mismo sitio.
   ============================================================ */
(() => {
  const svg = document.querySelector('.pmn-lupa');
  if (!svg || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const aro = svg.querySelector('.pmn-lupa__aro');
  const mango = svg.querySelector('.pmn-lupa__mango');
  const puntos = [...svg.querySelectorAll('.pmn-lupa__puntos circle')];
  const N = puntos.length;
  const C = 7.333;                 // centro (viewBox 16)
  const R0 = 6;                    // radio de la lupa
  const R1 = 7;                    // radio del aro de puntos
  const SW = parseFloat(getComputedStyle(svg).getPropertyValue('--pmn-lupa-sw')) || 1;
  const RD = 1.05;                 // radio del punto: el doble del trazo
  const MANGO = 4.37;
  const CICLO = 14;                // s, incluido el reposo
  const RETRASO = 1.5;             // s antes de la primera vuelta

  const ease = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  const tramo = (t, a, b) => ease((t - a) / (b - a));
  const mix = (a, b, k) => a + (b - a) * k;

  mango.style.strokeDasharray = `${MANGO} ${MANGO}`;

  const pintarAro = (r, sw, k) => {
    // k: 0 = aro entero, 1 = solo puntos
    const paso = (2 * Math.PI * r) / N;
    const raya = Math.max(paso * (1 - k), 0.001);
    aro.setAttribute('r', r);
    aro.style.strokeWidth = sw;
    aro.style.strokeDasharray = `${raya} ${paso - raya}`;
    aro.style.opacity = 1;
  };

  const pintarPuntos = (r, giroPlano, giroY, inclinacion) => {
    const cy = Math.cos(giroY), sy = Math.sin(giroY);
    const cx = Math.cos(inclinacion), sx = Math.sin(inclinacion);
    const d = 22; // distancia de la cámara: perspectiva suave
    puntos.forEach((p, i) => {
      const a = giroPlano + (i / N) * Math.PI * 2;
      let x = r * Math.cos(a), y = r * Math.sin(a), z = 0;
      // inclinación (eje X) y giro de la esfera (eje Y)
      [y, z] = [y * cx - z * sx, y * sx + z * cx];
      [x, z] = [x * cy + z * sy, -x * sy + z * cy];
      const s = d / (d - z);
      const prof = z / r; // -1 atrás, 0 de frente, 1 adelante
      p.setAttribute('cx', (C + x * s).toFixed(3));
      p.setAttribute('cy', (C + y * s).toFixed(3));
      // De frente (prof 0) el punto queda igual al de la raya que lo
      // originó, para que el cambio aro→puntos no salte.
      p.setAttribute('r', (RD * s * (1 + 0.4 * prof)).toFixed(3));
      p.setAttribute('opacity', (prof < 0 ? 1 + 0.7 * prof : 1).toFixed(2));
    });
  };

  const ocultarPuntos = () => puntos.forEach((p) => p.setAttribute('r', 0));

  const cuadro = (t) => {
    // mango
    const recoge = t < 4 ? tramo(t, 0, 0.45) : 1 - tramo(t, 9.1, 9.6);
    mango.style.strokeDashoffset = MANGO * recoge;

    if (t < 0.45 || t >= 9.6) {
      pintarAro(R0, SW, 0); ocultarPuntos(); return;
    }
    if (t < 1.0) { // el aro crece y engorda
      const k = tramo(t, 0.45, 1.0);
      pintarAro(mix(R0, R1, k), mix(SW, SW * 1.6, k), 0); ocultarPuntos(); return;
    }
    if (t < 1.12) { // aro → puntos, en un parpadeo
      const k = (t - 1.0) / 0.12;
      pintarAro(R1, mix(SW * 1.6, RD * 2, k), k); ocultarPuntos(); return;
    }
    if (t < 8.7) { // puntos girando en 3D
      aro.style.opacity = 0;
      let giroY, giroPlano, inclinacion, r;
      if (t < 4.6) { // primera vuelta: 1,5 giros de esfera
        const u = tramo(t, 1.12, 4.6);
        giroY = u * Math.PI * 3;
        giroPlano = u * Math.PI * 0.75;
        inclinacion = Math.sin(u * Math.PI) * 0.45;
        r = R1 * (1 - 0.18 * Math.sin(u * Math.PI));
      } else if (t < 5.2) { // pausa
        giroY = Math.PI * 3; giroPlano = Math.PI * 0.75; inclinacion = 0; r = R1;
      } else { // de vuelta, en el otro sentido
        const u = tramo(t, 5.2, 8.7);
        giroY = Math.PI * 3 * (1 - u);
        giroPlano = Math.PI * 0.75 * (1 - u);
        inclinacion = -Math.sin(u * Math.PI) * 0.45;
        r = R1 * (1 - 0.18 * Math.sin(u * Math.PI));
      }
      pintarPuntos(r, giroPlano, giroY, inclinacion); return;
    }
    if (t < 8.82) { // puntos → aro, en un parpadeo
      const k = 1 - (t - 8.7) / 0.12;
      pintarAro(R1, mix(SW * 1.6, RD * 2, k), k); ocultarPuntos(); return;
    }
    // el aro se encoge a lupa
    const k = tramo(t, 8.82, 9.4);
    pintarAro(mix(R1, R0, k), mix(SW * 1.6, SW, k), 0); ocultarPuntos();
  };

  const inicio = performance.now() + RETRASO * 1000;
  const FIN = 9.6; // de aquí al final del ciclo es la lupa quieta
  window.pmnLupaCuadro = cuadro; // para revisar cuadro a cuadro

  // Solo se redibuja mientras hay movimiento: en el reposo (y si la lupa
  // no se ve, p. ej. en la ficha de producto) no se pide ningún cuadro y
  // se espera con un temporizador al siguiente ciclo. Redibujar sin parar
  // obligaba al navegador a repintar 60 veces por segundo todo el rato.
  const bucle = (ahora) => {
    const t = (ahora - inicio) / 1000;
    const enCiclo = ((t % CICLO) + CICLO) % CICLO;
    const visible = svg.getClientRects().length > 0;

    const quieta = window.pmnLupaPausa || document.documentElement.classList.contains('pmn-search-open');
    if (t < 0 || enCiclo >= FIN || !visible || quieta) {
      if (t >= 0 && visible && !window.pmnLupaPausa) cuadro(FIN); // deja la lupa en reposo
      const espera = t < 0 ? -t : enCiclo >= FIN ? CICLO - enCiclo : 1;
      setTimeout(() => requestAnimationFrame(bucle), espera * 1000);
      return;
    }
    cuadro(enCiclo);
    requestAnimationFrame(bucle);
  };
  requestAnimationFrame(bucle);
})();

/* ============================================================
   Tinte de la barra de Safari (Antonio, 22-oct)
   Safari pinta su barra de arriba con el color de lo que esté pegado al
   borde superior de la página (un elemento fijo/sticky con fondo) y, si
   no encuentra nada, con <meta name="theme-color">.
   · Ficha de producto: el header va fijo arriba pero transparente, así
     que Safari caía al beige claro de la página y quedaba una franja de
     otro tono sobre la foto. Aquí se lee el color de fondo de la primera
     foto (esquina superior) y, con la página arriba del todo, se le pone
     al header como fondo (html.pmn-foto-arriba + --pmn-foto-fondo) y a
     theme-color. Al bajar se quitan los dos: la foto pasa por detrás de
     la barra de Safari. El primer color lo pone ya un script en línea
     de palamano-mobile-nav.liquid, para que no tarde.
   · Todas las páginas: al volver con «atrás» Safari restaura la página
     de memoria y se quedaba con el tinte de la anterior. En pageshow se
     recalcula theme-color con lo que haya de verdad arriba (en la
     portada, la barra amarilla de anuncios).
   ============================================================ */
(() => {
  const root = document.documentElement;
  const meta = document.querySelector('meta[name="theme-color"]');
  const BASE = meta?.content || '#f8f6f1';
  const esFicha = document.body.classList.contains('pmn-page-product');
  let colorFoto = null;

  const opaco = (c) => c && !/rgba\(.*,\s*0\)$/.test(c) && c !== 'transparent';

  // El primer fondo opaco que hay en el borde superior, mirando de
  // arriba hacia abajo en la pila (sin contar el propio header vacío).
  const colorArriba = () => {
    if (esFicha && colorFoto && root.classList.contains('pmn-foto-arriba')) return colorFoto;
    for (const el of document.elementsFromPoint(innerWidth / 2, 1)) {
      const bg = getComputedStyle(el).backgroundColor;
      if (opaco(bg)) return bg;
    }
    return BASE;
  };

  const pintar = () => {
    if (!meta) return;
    // En la ficha, ya desplazada, sin theme-color: así Safari no fija un
    // color y deja ver la foto pasando por detrás de su barra (vidrio).
    if (esFicha && !root.classList.contains('pmn-foto-arriba')) {
      meta.removeAttribute('content');
      return;
    }
    meta.content = colorArriba();
  };

  if (esFicha) {
    const img = document.querySelector('product-gallery img');
    // Solo con la página arriba del todo: en cuanto se baja, el header
    // vuelve a ser transparente y la foto sube por detrás de la barra de
    // Safari, en vez de esconderse tras un bloque de color (Antonio).
    const fotoArriba = () => {
      root.classList.toggle('pmn-foto-arriba', !!img && scrollY < 2);
    };

    if (img) {
      const u = new URL(img.currentSrc || img.src, location.href);
      u.searchParams.set('width', '40');
      fetch(u.href)
        .then((r) => r.blob())
        .then((b) => createImageBitmap(b))
        .then((bm) => {
          const c = document.createElement('canvas');
          c.width = bm.width;
          c.height = bm.height;
          const x = c.getContext('2d');
          x.drawImage(bm, 0, 0);
          const [r, g, b] = x.getImageData(1, 1, 1, 1).data;
          colorFoto = `rgb(${r}, ${g}, ${b})`;
          root.style.setProperty('--pmn-foto-fondo', colorFoto);
          fotoArriba();
          pintar();
        })
        .catch(() => {});
    }

    addEventListener('scroll', () => {
      const antes = root.classList.contains('pmn-foto-arriba');
      fotoArriba();
      if (antes !== root.classList.contains('pmn-foto-arriba')) pintar();
    }, { passive: true });
  }

  addEventListener('load', pintar);
  addEventListener('pageshow', () => requestAnimationFrame(pintar));
  pintar();
})();

/* ============================================================
   Buscador a pantalla completa (Antonio, 23-oct) — solo celular
   · Sube desde abajo: se cambia la animación de entrada/salida de este
     panel (el tema lo anima de lado). Se devuelve algo con then() y
     complete(), que es lo que usa el tema de sus propias animaciones.
   · Teclado abierto al entrar: iOS solo abre el teclado si el foco se
     da dentro del mismo toque, y en ese momento el panel aún no está
     visible. Se enfoca un campo temporal al tocar la lupa y, cuando el
     panel ya está abierto, el foco pasa al buscador (el teclado sigue).
   · html.pmn-search-open: la barra de abajo sigue visible y activa; tocar
     otro botón cierra el buscador y hace lo suyo (la lupa lo cierra).
   · Un solo flujo: «Buscar» en el teclado no salta a /search (que repetía
     los mismos resultados en otra página): solo cierra el teclado y deja
     los resultados en el panel.
   ============================================================ */
(() => {
  const panel = document.getElementById('search-drawer');
  if (!panel) return;

  const MOVIL = '(max-width: 699px)';
  const root = document.documentElement;
  const input = () => panel.querySelector('#search-drawer-input');

  const animar = (de, a) => {
    const content = panel.getShadowPartByName?.('content');
    const overlay = panel.getShadowPartByName?.('overlay');
    if (content) content.style.marginInlineStart = '0';
    const anims = [
      overlay?.animate({ opacity: de ? [0, 1] : [1, 0] }, { duration: 250, fill: 'both', easing: 'ease-in-out' }),
      content?.animate(
        { opacity: de ? [0, 1] : [1, 0], transform: de ? ['translateY(100%)', 'translateY(0)'] : ['translateY(0)', 'translateY(100%)'] },
        { duration: 450, fill: 'both', easing: 'cubic-bezier(.86,0,.07,1)' }
      ),
    ].filter(Boolean);
    const terminado = Promise.all(anims.map((x) => x.finished.catch(() => {})));
    return { then: (f, r) => terminado.then(f, r), complete: () => anims.forEach((x) => x.finish()) };
  };

  /* En escritorio, la animación del propio tema (se busca al abrir: el
     componente puede registrarse después de este script). */
  const delTema = (nombre) => Object.getPrototypeOf(panel)[nombre]?.call(panel);
  panel.createEnterAnimationControls = () =>
    matchMedia(MOVIL).matches ? animar(true) : delTema('createEnterAnimationControls') ?? animar(true);
  panel.createLeaveAnimationControls = () =>
    matchMedia(MOVIL).matches ? animar(false) : delTema('createLeaveAnimationControls') ?? animar(false);

  const i = input();
  if (i && matchMedia(MOVIL).matches) i.placeholder = '¿Qué estás buscando?';

  let temporal = null;
  document.addEventListener(
    'click',
    (e) => {
      if (!matchMedia(MOVIL).matches) return;
      if (!e.target.closest('.pmn a[aria-controls="search-drawer"]')) return;
      temporal = document.createElement('input');
      temporal.setAttribute('aria-hidden', 'true');
      temporal.style.cssText = 'position:fixed;top:40%;left:0;width:1px;height:1px;opacity:0;font-size:16px;';
      document.body.append(temporal);
      temporal.focus();
    },
    true
  );

  const pasarFoco = () => {
    input()?.focus();
    temporal?.remove();
    temporal = null;
  };

  panel.addEventListener(
    'submit',
    (e) => {
      if (!matchMedia(MOVIL).matches || !e.target.closest('#predictive-search-form')) return;
      e.preventDefault();
      input()?.blur();
    },
    true
  );

  /* Con el buscador abierto, cualquier otro botón de la barra lo cierra
     antes de hacer lo suyo (la lupa ya lo alterna sola). */
  document.addEventListener(
    'click',
    (e) => {
      if (!root.classList.contains('pmn-search-open')) return;
      const boton = e.target.closest('.pmn__item');
      if (!boton || boton.getAttribute('aria-controls') === 'search-drawer') return;
      panel.hide?.();
    },
    true
  );

  panel.addEventListener('dialog:before-show', () => {
    root.classList.add('pmn-search-open');
    setTimeout(pasarFoco, 60);
  });
  panel.addEventListener('dialog:after-show', pasarFoco);
  panel.addEventListener('dialog:before-hide', () => {
    root.classList.remove('pmn-search-open');
    temporal?.remove();
  });
})();
